import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, ChevronDown, ChevronRight, Building2 } from 'lucide-react';

// Same parameters as the Loads tab "Drop Off Trailers" kanban mode
const DROP_COLUMNS = ['At the Shipper', 'Waiting for Review', 'At the Delivery'];

const dropCategory = (l) => {
  const stops = l.stops || [];
  const dropStops = stops.map((s, i) => ({ s, i })).filter((x) => x.s.drop_off);
  if (!dropStops.length) return null;
  const last = dropStops[dropStops.length - 1];
  if (last.s.type === 'pickup') return 'At the Shipper';
  if (last.i === stops.length - 1) return 'At the Delivery';
  return 'Waiting for Review';
};

// Days dropped -> ready-for-PU (or today if still waiting)
const dropDays = (stop) => {
  if (!stop?.drop_off || !stop.trailer_dropped_date) return null;
  try {
    const start = new Date(stop.trailer_dropped_date);
    const end = stop.trailer_ready_for_pickup_date ? new Date(stop.trailer_ready_for_pickup_date) : new Date();
    return Math.max(0, Math.round((end - start) / 86400000));
  } catch {
    return null;
  }
};

const COLUMN_STYLE = {
  'At the Shipper': { dot: '#0a3a75', bg: '#eaf2fc', fg: '#0a3a75' },
  'Waiting for Review': { dot: '#a34e00', bg: '#fdf3d5', fg: '#8a6d00' },
  'At the Delivery': { dot: '#0c6b33', bg: '#dcf3e4', fg: '#0c6b33' },
};

export const TrailerDropOff = ({ loads = [] }) => {
  const navigate = useNavigate();
  const [openBucket, setOpenBucket] = useState(null);

  const buckets = useMemo(() => {
    const dropLoads = (loads || []).filter((l) => dropCategory(l));
    return DROP_COLUMNS.map((col) => ({
      col,
      items: dropLoads
        .filter((l) => dropCategory(l) === col)
        .map((l) => {
          const ds = (l.stops || []).filter((s) => s.drop_off && s.trailer_dropped_date);
          const lastDrop = ds[ds.length - 1];
          return { load: l, days: lastDrop ? dropDays(lastDrop) : null, facility: lastDrop?.facility_name };
        })
        .sort((a, b) => (b.days || 0) - (a.days || 0)),
    }));
  }, [loads]);

  const total = buckets.reduce((s, b) => s + b.items.length, 0);

  return (
    <div className="stats-card" style={{ padding: '1rem 1.25rem' }} data-testid="trailer-dropoff-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase text-[var(--tms-text)] flex items-center gap-1.5">
          <Truck className="w-4 h-4 text-[var(--tms-primary)]" />
          Trailer Drop Off
        </h3>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[var(--tms-border-light)] text-[var(--tms-text-secondary)]">
          {total}
        </span>
      </div>

      <div className="space-y-2">
        {buckets.map(({ col, items }) => {
          const cls = COLUMN_STYLE[col];
          const isOpen = openBucket === col;
          const visible = isOpen ? items : items.slice(0, 3);
          return (
            <div key={col} className="border border-[var(--tms-border-light)] rounded overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenBucket(isOpen ? null : col)}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[var(--tms-hover)] transition-colors"
                data-testid={`dropoff-toggle-${col.toLowerCase().replace(/ /g, '-')}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: cls.dot }} />
                  <span className="text-xs font-semibold" style={{ color: cls.fg }}>{col}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: cls.bg, color: cls.fg }}>
                    {items.length}
                  </span>
                </div>
                {items.length > 0 && (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
              </button>

              {items.length === 0 ? (
                <p className="px-3 py-1.5 text-[10px] text-[var(--tms-text-muted)] italic">No trailers</p>
              ) : (
                <div className="p-2 space-y-1 max-h-60 overflow-y-auto">
                  {visible.map(({ load, days, facility }) => (
                    <div
                      key={load.id}
                      className="flex items-center justify-between gap-2 px-2 py-1.5 rounded cursor-pointer text-xs hover:bg-[var(--tms-hover)] transition-colors"
                      style={{ background: cls.bg }}
                      onClick={() => navigate(`/loads/${load.id}`)}
                      data-testid={`dropoff-row-${load.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <span className="font-mono font-semibold">{load.load_number || load.id.substring(0, 8)}</span>
                        <div className="text-[10px] text-[var(--tms-text-muted)] truncate">
                          <Building2 className="w-2.5 h-2.5 inline mr-0.5" />
                          {facility || load.customer_name || '—'}
                        </div>
                      </div>
                      {days !== null && (
                        <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white" data-testid={`dropoff-days-${load.id}`}>
                          {days}d
                        </span>
                      )}
                    </div>
                  ))}
                  {!isOpen && items.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setOpenBucket(col)}
                      className="w-full text-center text-[10px] py-1 hover:underline font-medium"
                      style={{ color: cls.fg }}
                    >
                      + {items.length - 3} more
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
